import React, { useEffect, useState } from 'react'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import { DDGColors } from '../constants/colors'
import {
	HealthInstitution,
	WellnessClass,
	WellnessClassHour,
} from '../types/HealthInstitution'
import Image from './Image'
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react'
type Session = {
	id: string
	time: string
}

type DateSelectorSheetProps = {
	isOpen: boolean
	onClose: () => void
	onSelectSession: (session: Session) => void
	wellnessClass: WellnessClass
	activeFacility: HealthInstitution
}

const DateSelectorSheet: React.FC<DateSelectorSheetProps> = ({
	isOpen,
	onClose,
	onSelectSession,
	wellnessClass,
	activeFacility,
}) => {
	// Initialize with current month and 6th as selected date
	const today = new Date()
	const initialDate = new Date(today.getFullYear(), today.getMonth(), 6)
	const [selectedDate, setSelectedDate] = useState<Date>(initialDate)
	const [availableClassHours, setAvailableClassHours] = useState<
		WellnessClassHour[]
	>([])
	const [activeView, setActiveView] = useState<Date>(initialDate)
	const [activeDays, setActiveDays] = useState<Set<string>>(new Set())

	// Get sessions for the currently selected date
	useEffect(() => {
		if (!selectedDate) return

		// Find available class hours for the selected date
		const dayOfWeek = selectedDate
			.toLocaleDateString('en-US', { weekday: 'long' })
			.toLowerCase()
		const dateString = selectedDate.toISOString().split('T')[0]

		const availableHours = wellnessClass.classHours.filter((hour) => {
			if (hour.type === 'wday' && hour.day.toLowerCase() === dayOfWeek) {
				return true
			}
			if (hour.type === 'date' && hour.date) {
				const hourDate = new Date(hour.date)
				return hourDate.toISOString().split('T')[0] === dateString
			}
			return false
		})

		setAvailableClassHours(availableHours)
	}, [selectedDate, wellnessClass.classHours])

	// Calculate active days when the view changes (month navigation)
	useEffect(() => {
		calculateActiveDays(activeView)
	}, [activeView, wellnessClass.classHours])

	// Calculate active days for the current view
	const calculateActiveDays = (viewDate: Date) => {
		const activeDaysSet = new Set<string>()
		const year = viewDate.getFullYear()
		const month = viewDate.getMonth()

		// Get the first and last day of the month
		const firstDay = new Date(year, month, 1)
		const lastDay = new Date(year, month + 1, 0)
		const daysInMonth = lastDay.getDate()

		// Process weekday-based class hours
		wellnessClass.classHours.forEach((hour) => {
			if (hour.type === 'wday') {
				const weekdayNum = [
					'sunday',
					'monday',
					'tuesday',
					'wednesday',
					'thursday',
					'friday',
					'saturday',
				].indexOf(hour.day.toLowerCase())

				if (weekdayNum >= 0) {
					// Find all dates in current month that match this weekday
					for (let day = 1; day <= daysInMonth; day++) {
						const date = new Date(year, month, day)
						if (date.getDay() === weekdayNum) {
							activeDaysSet.add(date.toISOString().split('T')[0])
						}
					}
				}
			} else if (hour.type === 'date' && hour.date) {
				// For specific dates, check if they're in the current month
				const date = new Date(hour.date)
				if (date.getFullYear() === year && date.getMonth() === month) {
					activeDaysSet.add(date.toISOString().split('T')[0])
				}
			}
		})

		setActiveDays(activeDaysSet)
	}

	// Generate sessions from class hours
	const generateSessions = (): Session[] => {
		return availableClassHours.map((hour) => {
			const { from, to } = hour.interval
			const timeStr = `${from} - ${to}`

			const selectedDateObj = new Date(selectedDate)
			const timeDate = selectedDateObj
			timeDate.setHours(from.split(':').map(Number)[0])
			timeDate.setMinutes(from.split(':').map(Number)[1])

			return {
				id: `session-${hour.id}`,
				time: timeStr.replace(/:/g, ' : '), // Format time to match design
				classHourId: hour.id,
				timeDate,
			}
		})
	}

	const handleDateChange = (date: any) => {
		if (date instanceof Date) {
			setSelectedDate(date)
		}
	}

	// Handle calendar navigation (month change)
	const handleActiveStartDateChange = ({
		activeStartDate,
	}: {
		activeStartDate: any
	}) => {
		if (activeStartDate) {
			setActiveView(activeStartDate)
		}
	}

	// Custom tile content to match the design and show active days
	const tileClassName = ({ date, view }: { date: Date; view: string }) => {
		if (view === 'month') {
			// Check if current date is selected
			const isSelected =
				selectedDate.getDate() === date.getDate() &&
				selectedDate.getMonth() === date.getMonth() &&
				selectedDate.getFullYear() === date.getFullYear()

			if (isSelected) {
				return 'bg-teal-800 text-white rounded-full'
			}

			// Check if current date is one of the active days
			const dateString = date.toISOString().split('T')[0]
			if (!activeDays.has(dateString)) {
				return 'text-gray-400'
			}
		}
		return null
	}

	// Disable dates that don't have class hours
	const tileDisabled = ({ date, view }: { date: Date; view: string }) => {
		if (view !== 'month') return false

		const dateString = date.toISOString().split('T')[0]
		return !activeDays.has(dateString)
	}

	if (!isOpen) return null

	const sessions = generateSessions()
	return (
		<div className='fixed inset-0 z-50 flex flex-col justify-end bg-black/20'>
			<div className='flex flex-col bg-white rounded-t-3xl'>
				{' '}
				{/* Header with close button */}
				<div className='px-4 py-2 border-b border-gray-200'>
					<div className='flex items-center justify-between'>
						<h2 className='text-sm font-bold'>Select a Class Date</h2>
						<button onClick={onClose} className='p-2'>
							<XIcon size={24} />
						</button>
					</div>
				</div>
				{/* Calendar */}
				<div className='p-4'>
					<style>
						{`
          /* Custom styles for react-calendar to match design */
          .react-calendar {
            width: 100%;
            border: none;
            font-family: inherit;
          }

.react-calendar__tile:disabled {
              background-color: inherit;
              color: #d1d5db;
              cursor: not-allowed;
            }
          .react-calendar__month-view__weekdays__weekday {
            text-align: center;
            text-transform: uppercase;
            font-weight: bold;
            font-size: 0.875rem;
            padding: 0.75rem 0;
            abbr {
              text-decoration: none;
            }
          }
          .react-calendar__tile {
            padding: 0.75rem 0;
            font-size: 1rem;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 30px;
            background: transparent !important
          }
             .react-calendar__tile abbr {
           display: flex;
            justify-content: center;
            align-items: center;
            height: 30px;
            width: 30px;
            border-radius: 100%;
            
          }
          .react-calendar__tile--active abbr, 
          .react-calendar__tile--hasActive abbr {
           background: ${DDGColors.primary} !important;
            color: white !important;
            border-radius: 9999px;
            
          }
          .react-calendar__tile--now {
            background: inherit;
          }
          .react-calendar__month-view__days__day--weekend {
            color: inherit;
          }
        .react-calendar__navigation__arrow {
            border-radius: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
        }
        `}
					</style>
					<Calendar
						onChange={(val) => {
							handleDateChange(val)
						}}
						value={selectedDate}
						className='border-0'
						tileClassName={tileClassName}
						tileDisabled={tileDisabled}
						formatShortWeekday={(locale, date) =>
							date.toLocaleDateString('en', { weekday: 'short' }).toUpperCase()
						}
						formatDay={(locale, date) =>
							date.getDate() < 10 ? `0${date.getDate()}` : `${date.getDate()}`
						}
						minDate={new Date()}
						showNeighboringMonth={false}
						prevLabel={<ChevronLeftIcon />}
						nextLabel={<ChevronRightIcon />}
						prev2Label={null}
						next2Label={null}
						onActiveStartDateChange={handleActiveStartDateChange}
					/>
				</div>
				{/* Available Sessions */}
				<div className='flex-1 py-4 mb-24 space-y-4 overflow-y-scroll border-t border-gray-200 max-h-64'>
					{[...sessions].map((session) => (
						<div
						onClick={() => onSelectSession(session)}
							key={session.id}
							className={`px-2 border-l-4  border-l-primary cursor-pointer`}
						>
							<div className='flex items-start justify-between'>
								<div className='w-full'>
									<div className='flex flex-row items-center space-x-4'>
										<h3 className='text-sm font-bold'>
											{wellnessClass.title} Session
										</h3>
										<div className='px-2.5 py-1  rounded-full bg-primaryLight text-primary text-[10px]'>
											<p>
												<span className='font-bold'>
													{wellnessClass.capacity}
												</span>{' '}
												Slots left
											</p>
										</div>
									</div>
									<p className='text-xs text-gray-500'>{session.time}</p>
									<div className='w-full pb-1 mt-2 border-b border-b-[#EEEEEE]'>
										<p className='text-xs font-medium'>Class Instructor</p>
										<div className='flex flex-row items-center space-x-1'>
											{wellnessClass?.instructor ? (
												<div className='w-5 h-5'>
													<Image
									className='object-cover w-full h-full text-white text-[10px] flex items-center justify-center rounded-full bg-primary'
									src={
										[...(activeFacility?.classInstructors ?? [])].find(
											(instructor) => instructor.id === wellnessClass.instructor
										)?.image ?? ''
									}
									alt={'--'}
									useBase={false}
								/>
												</div>
											) : (
												<p>--</p>
											)}
											<p className='text-xs font-semibold text-dark'>
												{wellnessClass?.instructor
													? [...(activeFacility?.classInstructors ?? [])].find(
															(instructor) =>
																instructor.id === wellnessClass.instructor
													  )?.name
													: '--'}
											</p>
										</div>
									</div>
								</div>
								<p
									
									className='px-4 py-1.5 text-xs font-bold text-gray-600 border border-gray-300 rounded-full'
								>
									Select
								</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

export default DateSelectorSheet
